/* eslint-disable react-refresh/only-export-components -- Exports component and HOC */
/**
 * ErrorBoundary — Catches runtime errors in child components
 * Prevents the entire app from crashing on a single component error.
 *
 * Usage:
 *   <ErrorBoundary fallback={<p>Something went wrong</p>}>
 *     <MyComponent />
 *   </ErrorBoundary>
 *
 * Or with the HOC:
 *   export default withErrorBoundary(MyComponent, { level: 'feature' });
 */

import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';

interface ErrorBoundaryProps {
  /** Custom fallback UI to render on error */
  fallback?: ReactNode;
  /** Error level for logging: 'root' | 'feature' | 'component' */
  level?: 'root' | 'feature' | 'component';
  /** Component name for error reporting */
  componentName?: string;
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { level = 'component', componentName = 'Unknown', onError } = this.props;

    // Structured error logging for production monitoring
    console.error(
      `[ErrorBoundary:${level}] Error in ${componentName}:`,
      error,
      errorInfo.componentStack
    );

    // Forward to external error reporting (Sentry, LogRocket, etc.)
    if (onError) {
      onError(error, errorInfo);
    }
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { level = 'component' } = this.props;

      // Root-level error: show full-page error
      if (level === 'root') {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              gap: '16px',
              fontFamily: "'Open Sans', sans-serif",
              color: '#0c233c',
            }}
          >
            <h1 style={{ fontSize: '24px', fontWeight: 600 }}>Something went wrong</h1>
            <p style={{ fontSize: '14px', color: '#666666' }}>
              The application encountered an unexpected error.
            </p>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '8px 24px',
                backgroundColor: 'var(--primary-cobalt-blue)',
                color: 'var(--neutral-light-0)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Try Again
            </button>
          </div>
        );
      }

      // Feature-level error: show inline error card
      if (level === 'feature') {
        return (
          <div
            style={{
              padding: '24px',
              margin: '16px',
              backgroundColor: 'var(--primary-cobalt-blue-5)',
              border: '1px solid var(--primary-cobalt-blue-30)',
              borderRadius: '8px',
              textAlign: 'center',
              fontFamily: "'Open Sans', sans-serif",
            }}
          >
            <p style={{ fontSize: '14px', color: 'var(--neutral-light-70)', marginBottom: '12px' }}>
              This section encountered an error.
            </p>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '9px 16px',
                backgroundColor: 'var(--primary-cobalt-blue)',
                color: 'var(--neutral-light-0)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Retry
            </button>
          </div>
        );
      }

      // Component-level error: minimal inline fallback
      return (
        <span
          style={{
            display: 'inline-block',
            padding: '4px 8px',
            backgroundColor: 'var(--primary-cobalt-blue-5)',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#666666',
          }}
        >
          Error loading component
        </span>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC to wrap any component with an error boundary.
 * Useful for wrapping feature modules at import time.
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options: { level?: 'root' | 'feature' | 'component'; componentName?: string } = {}
): React.FC<P> {
  const displayName = options.componentName || WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const Wrapped: React.FC<P> = (props) => (
    <ErrorBoundary level={options.level || 'feature'} componentName={displayName}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  Wrapped.displayName = `withErrorBoundary(${displayName})`;
  return Wrapped;
}

export default ErrorBoundary;
